/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import { useAsyncError, useLocation, useNavigation } from 'react-router';
import type { Route } from './+types/_app.search';
import type { ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getLogger } from '@/lib/logger.server';
import CategoryPagination from '@/components/category-pagination';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinements from '@/components/category-refinements';
import CategorySorting from '@/components/category-sorting';
import ProductGridViewToggle from '@/components/product-grid-view-toggle';
import DeferredProductGrid from '@/components/product-grid';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import {
    getInitialFiltersOpen,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { useProductGridView } from '@/hooks/use-product-grid-view';

@PageType({
    name: 'Search Results Page',
    description: 'Search results page with product listings and personalized content',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'searchTopContent',
        name: 'Top Content Region',
        description: 'Content width region after the search title, above the filter bar and product grid',
        maxComponents: 5,
    },
])
export class SearchPageMetadata {}

export type SearchPageData = {
    searchTerm: string;
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>>;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    wishlistInitialState: Promise<WishlistInitialState>;
};

/**
 * Server-side loader function that fetches search results data.
 * This function runs on the server during SSR and prepares data for the search page.
 * @returns Object containing search results, refinements, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<SearchPageData> {
    const { context, request } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const offset = parseInt(searchParams.get('offset') || '0', 10);
    const q = searchParams.get('q') ?? '';
    const sort = searchParams.get('sort') ?? '';
    const refine = searchParams.getAll('refine');
    const initialFiltersOpen = getInitialFiltersOpen(searchParams, true);
    const config = getConfig(context);
    const logger = getLogger(context);

    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Search: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;

    const limit = config.search.products.hits.limit;

    // Page Designer content is visible above the filter bar. Start early so the
    // critical page fetch overlaps search work.
    const pagePromise = fetchPageWithComponentData(args, {
        pageId: 'search',
    });

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 2;
    const safeCriticalCount = Math.min(criticalCount, limit);

    logger.debug('Search: loader starting', { q, offset, sort, refineCount: refine.length });
    const searchResultCritical = await fetchSearchProducts(context, {
        q,
        limit: safeCriticalCount,
        offset,
        sort,
        refine,
        currency,
    });
    logger.info('Search: results loaded', { query: q, total: searchResultCritical.total, offset });

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);
    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;

    return {
        searchTerm: q,
        searchResultCritical,
        searchResultNonCritical: fetchSearchProducts(context, {
            q,
            limit: limit - effectiveCriticalCount,
            offset: offset + effectiveCriticalCount,
            sort,
            refine,
            currency,
        }),
        page: await pagePromise,
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
        wishlistInitialState: fetchWishlistInitialState(context),
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

export default function SearchPage({
    loaderData: {
        searchTerm,
        searchResultCritical,
        searchResultNonCritical,
        page,
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
        wishlistInitialState,
    },
}: {
    loaderData: SearchPageData;
}) {
    const { t } = useTranslation('search');
    const config = useConfig();

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen, true);
    const [gridView, setGridView] = useProductGridView();
    const limit = config.search.products.hits.limit;

    // Determine the maximum number of skeletons to display in the product grid.
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(8, limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedSearchRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInSearch({
                    searchInputText: searchTerm,
                    product,
                });
            }
        },
        [analytics, searchTerm]
    );

    useEffect(() => {
        // Only track if we haven't already tracked this search
        if (analyticsKey === lastTrackedSearchRef.current) {
            return;
        }
        lastTrackedSearchRef.current = analyticsKey;

        let cancelled = false;
        const trackedKey = analyticsKey;

        startTransition(() => {
            void nonCriticalPromise
                .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                    // Drop stale resolves after the user navigated to another search state.
                    if (cancelled || lastTrackedSearchRef.current !== trackedKey) {
                        return;
                    }
                    // Tealium: page-specific `search_view`. The adapter defers generic `view_page`
                    // until this succeeds; on rejection it emits a fallback (see README-TEALIUM).
                    void analytics.trackViewSearch({
                        searchInputText: searchTerm,
                        searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                        sort:
                            searchResultCritical.selectedSortingOption ||
                            searchResultCritical.sortingOptions?.[0]?.label ||
                            '',
                        refinements: searchResultCritical.selectedRefinements ?? {},
                    });
                })
                .catch(() => {
                    // Tealium adapter falls back to a degraded view_page on timeout.
                    // Einstein / Active Data simply miss this view_search.
                });
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, searchTerm, analyticsKey, nonCriticalPromise]);

    return (
        <WishlistProvider initialState={wishlistInitialState}>
            <SeoMeta
                title={
                    searchTerm
                        ? t('titleWithQuery', { query: searchTerm, defaultValue: `Results for "${searchTerm}"` })
                        : t('title', { defaultValue: 'Search' })
                }
                description={
                    searchTerm
                        ? t('meta.descriptionWithQuery', {
                              query: searchTerm,
                              count: searchResultCritical.total,
                              defaultValue: `${searchResultCritical.total} results for "${searchTerm}"`,
                          })
                        : t('meta.description', { defaultValue: 'Search our store for products' })
                }
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div className="section-container pb-16">
                <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h1 className="text-3xl font-bold leading-none tracking-[-0.75px] text-card-foreground flex items-end gap-2">
                        {t('results', {
                            query: searchTerm,
                            defaultValue: `Search result for ${searchTerm}`,
                        })}
                        <span className="text-xl">({searchResultCritical.total})</span>
                    </h1>
                </div>

                {/* searchTopContent — after title, before filter bar */}
                <Region page={page} regionId="searchTopContent" />

                {/* Desktop: single-row filter bar */}
                <div className="hidden lg:flex items-center gap-2 mb-4 border-b py-3">
                    <FiltersButton onClick={toggleFiltersOpen} isActive={filtersOpen} />
                    <ActiveFilters result={searchResultCritical} />
                    <div className="ml-auto flex items-center gap-2 shrink-0">
                        {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                            <CategorySorting result={searchResultCritical} />
                        )}
                        <ProductGridViewToggle gridView={gridView} onGridViewChange={setGridView} />
                    </div>
                </div>

                {/* Mobile/tablet: sort row + filters/active chips row */}
                <div className="lg:hidden flex flex-col gap-3 mb-4 border-b py-3">
                    {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                        <CategorySorting result={searchResultCritical} className="flex-1 min-w-0" />
                    )}
                    <div className="flex justify-between items-center gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <FiltersButton onClick={toggleFiltersOpen} isActive={filtersOpen} className="shrink-0" />
                            <ActiveFilters result={searchResultCritical} className="min-w-0 flex-1" />
                        </div>
                        <ProductGridViewToggle gridView={gridView} onGridViewChange={setGridView} />
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Category Refinements - toggles visibility on left side */}
                    {filtersOpen && (
                        <div className="w-full lg:w-64 lg:flex-shrink-0">
                            <CategoryRefinements result={searchResultCritical} refine={refine} />
                        </div>
                    )}
                    <div className="flex-grow">
                        <DeferredProductGrid
                            key={productGridDataKey}
                            critical={searchResultCritical.hits ?? []}
                            nonCritical={nonCriticalPromise}
                            nonCriticalCount={nonCriticalCount}
                            hasRefinementsPanel={filtersOpen}
                            gridView={gridView}
                            isLoading={isProductGridLoading}
                            handleProductClick={handleProductClick}
                            errorElement={<ProductGridError />}
                        />

                        {searchResultCritical.total > 1 && (
                            <div className="mt-10">
                                <CategoryPagination
                                    limit={limit}
                                    offset={searchResultCritical.offset}
                                    total={searchResultCritical.total}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </WishlistProvider>
    );
}
